/**
 * ProDecor product catalogue with **ex-VAT** trade prices.
 *
 * Application sequence:
 *   1. PRIMERS      — applied first
 *   2. FINISHES     — applied over primer (one of 6)
 *   3. PIGMENTS     — added INTO the finish (optional)
 *   4. WAXES/SEALERS — applied over finish (optional, choose one)
 *
 * Prices below are **ex-VAT** (divided by 1.2 from the Feb 2026 Walling PDF
 * which was VAT-inclusive) so they match the tier-based prices stored by
 * Set Material Costs. The calculator adds VAT back on at the total line.
 *
 * The `matchName` field on each pack is the exact Stock-catalogue name used
 * by the pricelist auto-seeder. When the installer opens the calculator we
 * fetch `/api/installer/prodecor-pricing` and any pack whose `matchName`
 * resolves to a live tier price uses that price instead of the fallback
 * hardcoded below — matches how ProBound Step 7 works.
 *
 * Packs with `matchName: null` are catalogue-only extras that Ox haven't
 * added to the printed pricelist yet (e.g. Venetian Stucco 24kg, Metallic
 * Concrete, OxWax Silver/Gold, OxPolyurethane/Polyaspartic Shield kits).
 * They fall back to the hardcoded ex-VAT price.
 */

export const PRIMERS = [
  {
    id: 'oxprime_p100',
    name: 'OxPrime P100',
    description: 'Multi-surface primer',
    unit: 'L',
    packs: [
      { size: 0.8, label: '0.8L', coverage_m2: 7, price: 9.33, matchName: 'Ox Primer P100 0.8L (7m² coverage)' },
      { size: 2.4, label: '2.4L', coverage_m2: 21, price: 22.75, matchName: 'Ox Primer P100 2.4L (21m² coverage)' },
      { size: 10.4, label: '10.4L', coverage_m2: 91, price: 82.83, matchName: 'Ox Primer P100 10.4L (91m² coverage)' },
    ],
  },
  {
    id: 'oxprime_p300',
    name: 'OxPrime P300',
    description: 'Heavy-duty primer',
    unit: 'L',
    packs: [
      { size: 0.8, label: '0.8L', coverage_m2: 7, price: 18.74, matchName: 'Ox Primer P300 0.8L (7m² coverage)' },
      { size: 2.4, label: '2.4L', coverage_m2: 21, price: 42.08, matchName: 'Ox Primer P300 2.4L (21m² coverage)' },
      { size: 10.4, label: '10.4L', coverage_m2: 91, price: 79.16, matchName: 'Ox Primer P300 10.4L (91m² coverage)' },
    ],
  },
  {
    id: 'oxprimer_epoxy_dpm',
    name: 'OxPrimer Epoxy DPM',
    description: 'Damp-proof epoxy primer',
    unit: 'pack',
    packs: [
      { size: 5, label: '5m² pack (5kg)', coverage_m2: 5, price: 51.33, weight_kg: 5, matchName: 'Epoxy DPM Primer 5kg Unit (10-15m² coverage)' },
      { size: 20, label: '20m² pack (15kg)', coverage_m2: 20, price: 138.83, weight_kg: 15, matchName: 'Epoxy DPM Primer 15kg Unit (50-75m² coverage)' },
      { size: 40, label: '40m² pack (25kg)', coverage_m2: 40, price: 212.33, weight_kg: 25, matchName: 'Epoxy DPM Primer 25kg (30-45m² coverage)' },
    ],
  },
];

export const FINISHES = [
  {
    id: 'venetian_stucco',
    name: 'Venetian Stucco',
    description: 'Classic Italian polished plaster effect',
    unit: 'kg',
    packs: [
      { size: 1, label: '1kg', coverage_m2: 5, price: 19.83, matchName: 'Venetian Stucco 1kg (2m² @ 3 coats)' },
      { size: 3, label: '3kg', coverage_m2: 15, price: 45.50, matchName: 'Venetian Stucco 3kg (6m² @ 3 coats)' },
      { size: 12, label: '12kg', coverage_m2: 60, price: 155.17, matchName: 'Venetian Stucco 12kg (24m² @ 3 coats)' },
      { size: 24, label: '24kg', coverage_m2: 120, price: 263.67, matchName: null },
    ],
  },
  {
    id: 'metallic_concrete',
    name: 'Concrete Metallic Deco Wall',
    description: 'Metallic concrete-effect wall finish',
    unit: 'kg',
    packs: [
      { size: 8, label: '8kg', coverage_m2: 8, price: 70.58, matchName: 'Concrete Metallic Deco Wall 8kg (8m² @ 1 coat)' },
      { size: 16, label: '16kg', coverage_m2: 16, price: 127.17, matchName: 'Concrete Metallic Deco Wall 16kg (16m² @ 1 coat)' },
    ],
  },
  {
    id: 'concrete',
    name: 'Concrete Wall Decorative Plaster',
    description: 'Classic concrete wall finish',
    unit: 'kg',
    packs: [
      { size: 8, label: '8kg', coverage_m2: 8, price: 59.50, matchName: 'Concrete Wall Decorative Plaster 8kg (8m² @ 1 coat)' },
      { size: 16, label: '16kg', coverage_m2: 16, price: 107.33, matchName: 'Concrete Wall Decorative Plaster 16kg (16m² @ 1 coat)' },
    ],
  },
  {
    id: 'metallic_stone_wall_light',
    name: 'Metallic Stone Wall — Light',
    description: 'Light-tone metallic stone wall finish',
    unit: 'L',
    packs: [
      { size: 1.6, label: '1.6L', coverage_m2: 12, price: 99.17, matchName: 'Metallic Stone Wall Structural Paint Light 1.6L (12m² @ 1 coat)' },
      { size: 9.6, label: '9.6L', coverage_m2: 72, price: 428.17, matchName: 'Metallic Stone Wall Structural Paint Light 9.6L (72m² @ 1 coat)' },
    ],
  },
  {
    id: 'metallic_stone_wall_dark',
    name: 'Metallic Stone Wall — Dark',
    description: 'Dark-tone metallic stone wall finish',
    unit: 'L',
    packs: [
      { size: 1.6, label: '1.6L', coverage_m2: 12, price: 99.17, matchName: 'Metallic Stone Wall Structural Paint Dark 1.6L (12m² @ 1 coat)' },
      { size: 9.6, label: '9.6L', coverage_m2: 72, price: 428.17, matchName: 'Metallic Stone Wall Structural Paint Dark 9.6L (72m² @ 1 coat)' },
    ],
  },
  {
    id: 'pearl_stucco',
    name: 'Pearl Stucco',
    description: 'Pearlescent stucco wall finish',
    unit: 'L',
    packs: [
      { size: 0.8, label: '0.8L', coverage_m2: 4, price: 39.67, matchName: 'Pearl Stucco Matt Wall 0.8L (4m² coverage)' },
      { size: 2.4, label: '2.4L', coverage_m2: 12, price: 98.58, matchName: 'Pearl Stucco Matt Wall 2.4L (12m² coverage)' },
      { size: 10.4, label: '10.4L', coverage_m2: 52, price: 364.58, matchName: 'Pearl Stucco Matt Wall 10.4L (52m² coverage)' },
    ],
  },
];

export const PIGMENTS = {
  small: {
    label: '20ml pigment',
    size_ml: 20,
    colours: ['Black', 'Tan', 'Dark Brown', 'Navy Blue'],
    price: 3.50,
    matchName: '20ml Walling Liquid Pigment (any colour)',
    doses_per_kg_finish: null, // TBD
  },
  large_stock: {
    label: '1L pigment (stock colour)',
    size_ml: 1000,
    colours: ['Black', 'Tan', 'Dark Brown', 'Navy Blue'],
    price: 110.83,
    matchName: 'Bespoke Pigment 1L (specify shade/number)',
    doses_per_kg_finish: null, // TBD
  },
  large_bespoke: {
    label: '1L pigment (bespoke colour)',
    size_ml: 1000,
    custom_colour: true,
    price: 119.17,
    matchName: 'Bespoke Pigment 1L (specify shade/number)',
    doses_per_kg_finish: null, // TBD
  },
};

// All waxes share the same coverage rate: 0.25kg = 16 m² → 64 m²/kg
export const WAXES = [
  {
    id: 'oxwax_clear',
    name: 'OxWax Clear',
    description: 'Clear protective wax',
    unit: 'kg',
    packs: [
      { size: 0.25, label: '0.25kg', coverage_m2: 16, price: 12.83, matchName: 'Ox Wax Transparent 0.25kg (16m² coverage)' },
      { size: 1, label: '1kg', coverage_m2: 64, price: 43.75, matchName: 'Ox Wax Transparent 1kg (64m² coverage)' },
      { size: 3, label: '3kg', coverage_m2: 192, price: 113.17, matchName: 'Ox Wax Transparent 3kg (192m² coverage)' },
    ],
  },
  {
    id: 'oxwax_silver',
    name: 'OxWax Silver',
    description: 'Silver-tinted decorative wax',
    unit: 'kg',
    packs: [
      { size: 0.25, label: '0.25kg', coverage_m2: 16, price: 15.75, matchName: 'Ox Wax Pearl Silver 0.25kg (16m² coverage)' },
    ],
  },
  {
    id: 'oxwax_gold',
    name: 'OxWax Gold',
    description: 'Gold-tinted decorative wax',
    unit: 'kg',
    packs: [
      { size: 0.25, label: '0.25kg', coverage_m2: 16, price: 15.75, matchName: 'Ox Wax Pearl Gold 0.25kg (16m² coverage)' },
    ],
  },
];

export const SEALERS = [
  {
    id: 'oxlazur_l100',
    name: 'OxLazur L100',
    description: 'Water-based protective sealer',
    unit: 'L',
    packs: [
      // Coverage: 0.8L = 8m² → 10 m²/L
      { size: 0.8, label: '0.8L', coverage_m2: 8, price: 17.50, matchName: 'Ox Lazur L100 0.8L (8m² coverage)' },
      { size: 2.4, label: '2.4L', coverage_m2: 24, price: 42.58, matchName: 'Ox Lazur L100 2.4L (24m² coverage)' },
      { size: 10.4, label: '10.4L', coverage_m2: 104, price: 157.50, matchName: 'Ox Lazur L100 10.4L (104m² coverage)' },
    ],
  },
  {
    id: 'oxpolyurethane_shield',
    name: 'OxPolyurethane Shield',
    description: 'Polyurethane protective coating',
    unit: 'pack',
    finishes: ['Gloss', 'Satin', 'Matt'],
    packs: [
      // Rough weight estimates — 2-part kit
      { size: 5, label: '5m² pack', coverage_m2: 5, price: 41.66, weight_kg: 2, matchName: 'Polyurethane 2-Part Shield Topcoat Satin (10m² coverage)' },
      { size: 20, label: '20m² pack', coverage_m2: 20, price: 124.99, weight_kg: 6, matchName: 'Polyurethane 2-Part Shield Topcoat Satin (40m² coverage)' },
    ],
  },
  {
    id: 'oxpolyaspartic_shield',
    name: 'OxPolyaspartic Shield',
    description: 'High-performance polyaspartic coating',
    unit: 'pack',
    finishes: ['Gloss'],
    packs: [
      { size: 5, label: '5m² pack', coverage_m2: 5, price: 28.74, weight_kg: 2, matchName: null },
    ],
  },
];

export const PRODECOR_PRIMARY = '#D5AB09';
export const PRODECOR_SECONDARY = '#2B2101';

/** Every Stock-catalogue name the ProDecor calculator can price — used to tag rows on the Pricing page. */
export function prodecorMatchNames() {
  const out = new Set();
  const walk = (v) => {
    if (!v || typeof v !== 'object') return;
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (typeof v.matchName === 'string' && v.matchName) out.add(v.matchName);
    Object.values(v).forEach((x) => { if (x && typeof x === 'object') walk(x); });
  };
  walk([PRIMERS, FINISHES, PIGMENTS, WAXES, SEALERS]);
  return [...out];
}
